import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment } from "@opensea/graphql"
import type { Nullable } from "@opensea/utils"
import type { CurrencyIdentifier } from "@opensea/wallet"
import type { QueryTrait } from "../search/construct-traits"

export const collectionRecentlyMintedFragment = graphql(`
  fragment isRecentlyMinted on Collection {
    createdAt
  }
`)

export function getIsRecentlyMinted(
  key: FragmentOf<typeof collectionRecentlyMintedFragment>,
): boolean {
  const collection = readFragment(collectionRecentlyMintedFragment, key)
  const now = new Date()
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000) // 7 days in milliseconds
  return new Date(collection.createdAt) > sevenDaysAgo
}

export const recentlyCreatedCurrencyFragment = graphql(`
  fragment isRecentlyCreated on Currency {
    genesisDate
  }
`)

export function getIsRecentlyCreatedCurrency(
  key: FragmentOf<typeof recentlyCreatedCurrencyFragment>,
): boolean {
  const currency = readFragment(recentlyCreatedCurrencyFragment, key)
  const now = new Date()
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000) // 7 days in milliseconds
  const createdAt = currency.genesisDate
  return createdAt ? new Date(createdAt) > sevenDaysAgo : false
}

export const collectionUrlFragment = graphql(`
  fragment collectionUrl on CollectionIdentifier {
    slug
  }
`)

export function getCollectionUrl(
  key: FragmentOf<typeof collectionUrlFragment>,
  locale?: string,
): string {
  const collection = readFragment(collectionUrlFragment, key)
  return getCollectionUrlBySlug(collection.slug, undefined, locale)
}

export function getCollectionUrlBySlug(
  slug: string,
  tab?: string,
  locale?: string,
) {
  const basePath = locale ? `/${locale}` : ""
  return tab
    ? `${basePath}/collection/${slug}/${tab}`
    : `${basePath}/collection/${slug}`
}

export const CollectionBannerImageFragment = graphql(`
  fragment CollectionBannerImage on Collection {
    featuredImageUrl
    bannerImageUrl
    imageUrl
  }
`)

export function getCollectionBannerImage(
  key: Nullable<FragmentOf<typeof CollectionBannerImageFragment>>,
): string | undefined | null {
  const collection = readFragment(CollectionBannerImageFragment, key)
  // TODO for now default to featured image url for merchandising pages
  return collection?.bannerImageUrl || collection?.featuredImageUrl
}

export function getCollectionBannerFallbackImage(
  key: Nullable<FragmentOf<typeof CollectionBannerImageFragment>>,
): string | undefined | null {
  const collection = readFragment(CollectionBannerImageFragment, key)
  return collection?.imageUrl
}

export const CollectionNativeCurrencyIdentifierFragment = graphql(`
  fragment CollectionNativeCurrencyIdentifier on Collection {
    chain {
      identifier
      nativeCurrency {
        address
      }
    }
  }
`)

export function getCollectionNativeCurrencyIdentifier(
  key: FragmentOf<typeof CollectionNativeCurrencyIdentifierFragment>,
): CurrencyIdentifier {
  const collection = readFragment(
    CollectionNativeCurrencyIdentifierFragment,
    key,
  )
  return {
    chainIdentifier: collection.chain.identifier,
    contractAddress: collection.chain.nativeCurrency.address,
  }
}

export function getCollectionUrlByTrait(
  collectionSlug: string,
  trait: QueryTrait,
  locale?: string,
) {
  return `${getCollectionUrlBySlug(collectionSlug, undefined, locale)}?traits=${JSON.stringify(
    [
      {
        traitType: encodeURIComponent(trait.traitType),
        values: trait.values.map(value => encodeURIComponent(value)),
      },
    ],
  )}`
}

export function getCollectionOverviewUrl(slug: string) {
  return `${getCollectionUrlBySlug(slug)}/overview`
}
